import { useEffect, useState, useContext } from 'react'

import { Box, Typography, IconButton } from '@mui/material'
import { IconPlus, IconColumns2, IconColumns3 } from '@tabler/icons-react'

import { routes, Link } from '@redwoodjs/router'

import { getDayObjectsByType } from 'src/components/Objects/searchIndex'
import { SimpleNavItem } from 'src/layouts/ModernLayout/CollapsibleNavSection'
import { DayContext } from 'src/lib/dayContext'
import { NativeObjectTypes } from 'src/lib/objects'
import { NativeSuggestedPipelineTypes } from 'src/lib/relationshipSummary'

interface PipelineNavItemsProps {
  workspaceId: string
}

const PipelineNavItems: React.FC<PipelineNavItemsProps> = ({ workspaceId }) => {
  const [pipelines, setPipelines] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadPipelines = async () => {
      if (!workspaceId) return

      try {
        const pipelineSearchEntries = await getDayObjectsByType({
          objectType: NativeObjectTypes.Pipeline,
          workspaceId,
        })

        // Sort by updatedAt descending to get most recently updated
        const sortedPipelines = pipelineSearchEntries.sort((a, b) => {
          const dateA = new Date(a.updatedAt || 0).getTime()
          const dateB = new Date(b.updatedAt || 0).getTime()
          return dateB - dateA
        })

        setPipelines(sortedPipelines.slice(0, 5)) // Get top 5
      } catch (error) {
        console.error('Error loading pipelines:', error)
        setPipelines([])
      } finally {
        setLoading(false)
      }
    }

    loadPipelines()
  }, [workspaceId])

  if (loading) {
    return (
      <Box sx={{ px: 2, py: 1 }}>
        <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
          Loading...
        </Typography>
      </Box>
    )
  }

  return (
    <>
      {pipelines.map((pipelineEntry) => {
        const pipelineType =
          pipelineEntry?.object?.properties?.standard?.type?.value

        return (
          <SimpleNavItem
            key={pipelineEntry.objectId}
            label={pipelineEntry.label || 'Untitled Pipeline'}
            route={routes.objectDetail({
              workspaceId: workspaceId,
              objectTypeSlug: 'pipelines',
              objectId: pipelineEntry.objectId,
            })}
            icon={
              NativeSuggestedPipelineTypes[pipelineType]?.icon ? (
                NativeSuggestedPipelineTypes[pipelineType].icon
              ) : (
                <IconColumns2 size={18} />
              )
            }
          />
        )
      })}
      <SimpleNavItem
        key="all-pipelines"
        label="Manage Pipelines"
        route={routes.pipelines()}
        icon={<IconColumns3 size={18} />}
      />
    </>
  )
}

export default PipelineNavItems

export const PipelineHeaderAction = () => {
  const { selectedWorkspace } = useContext(DayContext)

  return (
    <IconButton
      component={Link}
      to={routes.objectDetail({
        workspaceId: selectedWorkspace,
        objectTypeSlug: 'pipelines',
        objectId: 'new',
      })}
      size="small"
      sx={{
        width: 24,
        height: 24,
        p: 0,
        opacity: 0.7,
        '&:hover': {
          opacity: 1,
        },
      }}
      onClick={(e) => {
        e.stopPropagation()
      }}
    >
      <IconPlus size={16} />
    </IconButton>
  )
}
